import React from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { Link as UiComponentsLink, Button } from '@placer-ui/components';
import { useHasAnonymousUserPermissions } from 'store/selectors/hooks/anonymous-user-permissions';
import { useRouteParam } from 'hooks/use-query/use-query';
import { useSplunkCallback } from 'hooks/use-splunk-callback/use-splunk-callback';
import { ssoBaseSplunkData } from 'features/authentication/utils/constants';
import {
    SSO_LOGIN_ROUTE,
    SSO_SIGNUP_ROUTE,
    ssoSplunkActions,
} from 'features/authentication/sso/constants';
import classnames from 'classnames';
import styles from './sso-link.module.scss';

type SourcePage = 'login' | 'signup';
interface SsoLinkProps {
    sourcePage: SourcePage;
    leanVersion?: boolean;
}

export const SsoLink = ({ sourcePage, leanVersion = false }: SsoLinkProps) => {
    const hasSsoPermission = useHasAnonymousUserPermissions('enable_sso');
    const next_url = useRouteParam('next_url');
    const sendEvent = useSplunkCallback(ssoBaseSplunkData);
    const isLoginSourcePage = sourcePage === 'login';
    const history = useHistory();
    const location = useLocation();

    const sendSplunkEvent = () =>
        sendEvent({
            action: isLoginSourcePage
                ? ssoSplunkActions.loginWithSsoLink
                : ssoSplunkActions.signupWithSsoLink,
        });

    const handleClick = () => {
        sendSplunkEvent();
        const pathname = isLoginSourcePage ? SSO_LOGIN_ROUTE : SSO_SIGNUP_ROUTE;
        history.push({
            pathname,
            search: location.search,
            state: { next_url },
        });
    };

    const ssoLinkInlineElement = (
        <div
            className={classnames(styles.ssoButton, {
                [styles.inlineVersion]: leanVersion,
            })}
        >
            <UiComponentsLink
                onClick={handleClick}
                size="medium"
                weight="semi-bold"
                hideUnderline={false}
            >
                SSO
            </UiComponentsLink>
        </div>
    );

    const SsoLinkElement =
        <Button testId="sso-login-button" onClick={handleClick} fullWidth variant="secondary-blue">
            Login with SSO
        </Button>;

    const ssoLinkElement = leanVersion ? ssoLinkInlineElement : SsoLinkElement;
    return <>{hasSsoPermission && ssoLinkElement}</>;
};
